import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { track } from "@/lib/track";

/**
 * Voice2Evidence Corporate Briefing — horizontal slide carousel.
 * Uses the console light palette (warm parchment cream, white surfaces, navy ink,
 * rust accent) rather than the dark public-site palette.
 *
 * 16 slides arranged in a horizontal flex track. Each slide takes the full
 * viewport; the track translates left/right between slides on next/prev.
 * Body scroll is locked while the deck is mounted.
 */

// Stable slide order — must match the rendered <Section> ids below.
const SLIDE_IDS = [
  "title",
  "problem",
  "shift",
  "what-we-do",
  "pentavoice",
  "how-it-works",
  "engagement-flow",
  "pillars-lifecycle",
  "for-ma",
  "for-heor",
  "outputs",
  "methodology",
  "published-work",
  "engage",
];

// ----- Slide primitives -------------------------------------------------------
//
// Horizontal carousel mode: each <Section> is a full-viewport slide that lives
// in a horizontal flex track. The deck transforms its track left/right between
// slides — no vertical page scroll is involved any more.
//
// Each slide is INTERNALLY scrollable in the rare case content is taller than
// the viewport (e.g. very small screens, large user-zoom), so nothing gets
// clipped without recourse.

const Section = ({ id, eyebrow, num, children, bg = "cream", first = false, compact = false }) => {
  const bgClass = bg === "white" ? "bg-white" : "bg-[#FAF7F1]";
  // Top padding stays identical across all slides so the eyebrow row aligns.
  // `compact` only tightens the BOTTOM padding + header margin so dense slides
  // (e.g. published-work) can fit shorter laptop viewports without scroll.
  const pad = compact
    ? "pt-20 pb-5 lg:pt-24 lg:pb-6"
    : "pt-20 pb-12 lg:pt-24 lg:pb-14";
  const headMb = compact ? "mb-3 lg:mb-4" : "mb-8 lg:mb-10";
  return (
    <section
      id={id}
      className={`w-screen h-full shrink-0 overflow-y-auto ${bgClass} ${
        first ? "" : "border-l border-[#E8E1D2]"
      }`}
      data-testid={`deck-section-${id}`}
    >
      <div className={`min-h-full max-w-[1280px] mx-auto px-8 lg:px-16 ${pad}`}>
        <div className={`flex items-center justify-between ${headMb}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#C84C21]">
            {eyebrow}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A09680]">
            {num} / 14
          </div>
        </div>
        {children}
      </div>
    </section>
  );
};

const Display = ({ children, size = "l" }) => {
  const sz =
    size === "xl"
      ? "text-5xl md:text-6xl lg:text-7xl"
      : size === "l"
      ? "text-4xl md:text-5xl lg:text-[3.5rem]"
      : "text-3xl md:text-4xl lg:text-5xl";
  return (
    <h2
      className={`font-serif font-normal ${sz} text-[#0F1825] leading-[1.05] tracking-tight`}
    >
      {children}
    </h2>
  );
};

const Body = ({ children, className = "" }) => (
  <p className={`text-[16.5px] leading-relaxed text-[#3F4856] ${className}`}>
    {children}
  </p>
);

const Dim = ({ children, className = "" }) => (
  <p className={`text-[14.5px] leading-relaxed text-[#5C6470] ${className}`}>
    {children}
  </p>
);

const TwoCol = ({ left, right }) => (
  <div className="grid grid-cols-12 gap-x-10 gap-y-10">
    <div className="col-span-12 lg:col-span-6">{left}</div>
    <div className="col-span-12 lg:col-span-6">{right}</div>
  </div>
);

// ----- Page -------------------------------------------------------------------

export default function Deck() {
  const [activeIdx, setActiveIdx] = useState(0);

  // The deck sits outside the public Layout, so record its own page view + conversion.
  useEffect(() => {
    track("pageview", "/deck");
    track("deck_opened", "/deck");
  }, []);

  // Lock the body so vertical page scroll never appears — the deck owns the
  // viewport. Restore on unmount.
  useEffect(() => {
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    const prevOverflow = document.body.style.overflow;
    document.body.style.backgroundColor = "#FAF7F1";
    document.documentElement.style.backgroundColor = "#FAF7F1";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const goTo = useCallback((idx) => {
    if (idx < 0 || idx >= SLIDE_IDS.length) return;
    setActiveIdx(idx);
  }, []);

  const prev = useCallback(() => goTo(activeIdx - 1), [activeIdx, goTo]);
  const next = useCallback(() => goTo(activeIdx + 1), [activeIdx, goTo]);

  // Keyboard shortcuts — ← / →
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goTo(SLIDE_IDS.length - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, goTo]);

  const isFirst = activeIdx === 0;
  const isLast = activeIdx === SLIDE_IDS.length - 1;

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#FAF7F1] text-[#0F1825]" data-testid="page-deck">
      {/* Floating slide navigation — left / right chevrons + slide counter */}
      <button
        type="button"
        onClick={prev}
        disabled={isFirst}
        aria-label="Previous slide"
        data-testid="deck-nav-prev"
        className={`fixed left-4 md:left-6 top-1/2 -translate-y-1/2 z-50
          w-12 h-12 flex items-center justify-center
          bg-white border border-[#E8E1D2] shadow-sm
          transition-all duration-200
          ${isFirst
            ? "opacity-0 pointer-events-none -translate-x-2"
            : "opacity-100 hover:border-[#C84C21] hover:text-[#C84C21] hover:shadow-md"
          }`}
      >
        <ChevronLeft size={20} strokeWidth={1.5} />
      </button>
      <button
        type="button"
        onClick={next}
        disabled={isLast}
        aria-label="Next slide"
        data-testid="deck-nav-next"
        className={`fixed right-4 md:right-6 top-1/2 -translate-y-1/2 z-50
          w-12 h-12 flex items-center justify-center
          bg-white border border-[#E8E1D2] shadow-sm
          transition-all duration-200
          ${isLast
            ? "opacity-0 pointer-events-none translate-x-2"
            : "opacity-100 hover:border-[#C84C21] hover:text-[#C84C21] hover:shadow-md"
          }`}
      >
        <ChevronRight size={20} strokeWidth={1.5} />
      </button>

      {/* Slide counter pill — bottom-centre */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50
          bg-white border border-[#E8E1D2] shadow-sm
          px-4 py-2 flex items-center gap-3
          font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470]"
        data-testid="deck-slide-counter"
      >
        <span className="text-[#C84C21] font-medium">
          {String(activeIdx + 1).padStart(2, "0")}
        </span>
        <span className="text-[#A09680]">/ {SLIDE_IDS.length}</span>
        <span className="hidden md:inline w-px h-3 bg-[#E8E1D2]" />
        <span className="hidden md:inline text-[#A09680] normal-case tracking-normal text-[10.5px]">
          ←&nbsp;/&nbsp;→&nbsp; to navigate
        </span>
      </div>
      {/* Top bar — fixed at top, sits above the horizontal slide track */}
      <header className="absolute top-0 left-0 right-0 bg-white/95 backdrop-blur border-b border-[#E8E1D2] z-40">
        <div className="max-w-[1280px] mx-auto px-8 lg:px-16 py-3 flex items-center justify-between gap-6">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="deck-back-home"
          >
            <ArrowLeft size={14} className="text-[#5C6470] group-hover:text-[#C84C21] transition-colors" />
            <img
              src="/assets/logo/v2e-logo-light.png"
              alt="Voice2Evidence"
              className="h-6 w-auto"
            />
            <span className="hidden md:inline font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#A09680]">
              Corporate briefing
            </span>
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#C84C21] hover:bg-[#A93D17] text-white font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2.5 transition-colors"
            data-testid="deck-cta-briefing-top"
          >
            Request a briefing →
          </Link>
        </div>
      </header>

      {/* Horizontal slide track — translates left/right between slides */}
      <div
        className="absolute inset-0 flex transition-transform duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform"
        style={{
          width: `${SLIDE_IDS.length * 100}vw`,
          transform: `translateX(-${activeIdx * 100}vw)`,
        }}
        data-testid="deck-track"
      >
        {/* 01 — Title */}
        <Section id="title" eyebrow="01 · Corporate briefing" num="01" bg="white" first>
        <div className="grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-9">
            <img
              src="/assets/logo/v2e-logo-light.png"
              alt="Voice2Evidence"
              className="h-14 w-auto mb-10"
            />
            <div className="w-16 h-[2px] bg-[#C84C21] mb-10"></div>
            <h1 className="font-serif font-normal text-5xl md:text-6xl lg:text-[5.5rem] text-[#0F1825] leading-[1.04] tracking-tight">
              Patient voice,
              <span className="block italic">translated into evidence.</span>
            </h1>
            <p className="mt-10 text-[#5C6470] text-lg leading-relaxed max-w-2xl">
              A corporate briefing for Medical Affairs, HEOR teams and Patient Engagement teams.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#A09680]">
              An initiative by
            </div>
            <div className="font-serif text-[1.4rem] text-[#0F1825] mt-2 leading-tight">
              MarksMan Healthcare
            </div>
            <div className="mt-8 font-mono text-[9px] uppercase tracking-[0.24em] text-[#A09680]">
              Confidential · For discussion purposes
            </div>
          </div>
        </div>
      </Section>

      {/* 02 — The Problem */}
      <Section id="problem" eyebrow="02 · The problem" num="02">
        <TwoCol
          left={
            <Display>
              Patient voice is everywhere.
              <span className="block italic">Evidence is not.</span>
            </Display>
          }
          right={
            <div className="space-y-5 lg:pt-3">
              <Body>
                Patient insights are scattered across social conversations, patient communities, advisory discussions, real-world evidence, and published research.
              </Body>
              <Body>
                Most organisations struggle to translate these fragmented signals into actionable strategy.
              </Body>
              <p className="pt-5 mt-5 border-t border-[#E8E1D2] font-serif italic text-2xl text-[#0F1825]">
                Voice2Evidence bridges that gap.
              </p>
            </div>
          }
        />

        {/* Figure — the evidence gap (signal volume vs. evidence extracted) */}
        <div className="mt-8 pt-6 border-t border-[#E8E1D2]" data-testid="deck-fig-evidence-gap">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">
              The evidence gap · illustrative
            </div>
            <div className="flex items-center gap-4 font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-3.5 h-2 bg-[#C84C21]/25" /> Signal volume
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-3.5 h-2 bg-[#0F1825]" /> Evidence extracted
              </span>
            </div>
          </div>
          <div className="space-y-2.5">
            {[
              { label: "Social & digital conversations", signal: 92, evidence: 8 },
              { label: "Patient communities & forums", signal: 78, evidence: 12 },
              { label: "Advisory & qualitative research", signal: 55, evidence: 30 },
              { label: "Real-world data & registries", signal: 40, evidence: 22 },
              { label: "Published patient experience", signal: 25, evidence: 20 },
            ].map(({ label, signal, evidence }) => (
              <div key={label} className="grid grid-cols-12 items-center gap-4">
                <div className="col-span-5 md:col-span-4 text-[12.5px] text-[#3F4856]">
                  {label}
                </div>
                <div className="col-span-6 md:col-span-7 h-3.5 bg-[#F3EDDF] relative">
                  <div className="absolute inset-y-0 left-0 bg-[#C84C21]/25" style={{ width: `${signal}%` }} />
                  <div className="absolute inset-y-0 left-0 bg-[#0F1825]" style={{ width: `${evidence}%` }} />
                </div>
                <div className="col-span-1 font-mono text-[9.5px] tracking-[0.14em] text-[#A09680] text-right">
                  {evidence}/{signal}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 text-[11.5px] italic text-[#A09680]">
            Most patient signals never reach decision-grade evidence — Voice2Evidence closes that gap.
          </div>
        </div>
      </Section>

      {/* 03 — The Shift */}
      <Section id="shift" eyebrow="03 · The shift" num="03" bg="white">
        <TwoCol
          left={
            <Display>
              Patient voice is becoming a{" "}
              <span className="italic">strategic evidence layer</span> for pharma.
            </Display>
          }
          right={
            <div className="space-y-5 lg:pt-3">
              <Body>Patient voice is no longer just a soft insight stream.</Body>
              <Body>
                When structured with discipline, it can connect Medical Affairs, HEOR, Market Access, Regulatory Affairs, and Patient Engagement around a shared understanding of patient experience.
              </Body>
            </div>
          }
        />
      </Section>

      {/* 04 — What we do */}
      <Section id="what-we-do" eyebrow="04 · What we do" num="04">
        <TwoCol
          left={
            <Display>
              Built as a service.
              <span className="block italic">Patient voice, operationalised.</span>
            </Display>
          }
          right={
            <div className="space-y-5 lg:pt-3">
              <Body>
                Voice2Evidence is an AI-enabled, expert-validated services engagement scoped around your asset, indication, geography, and lifecycle stage.
              </Body>
              <Body>
                Especially valuable for late-stage, launch, and post-launch brands — where patient experience can directly shape evidence, access, medical, and engagement strategy.
              </Body>
            </div>
          }
        />
      </Section>

      {/* 05 — PentaVoice */}
      <Section id="pentavoice" eyebrow="05 · The framework" num="05" bg="white">
        <TwoCol
          left={
            <div>
              <Display size="xl">
                PentaVoice<sup className="text-[0.32em] align-super ml-1 text-[#C84C21]">™</sup>
              </Display>
              <p className="mt-5 font-serif italic text-2xl text-[#0F1825]/80 max-w-md">
                Five evidence streams. One patient-evidence narrative.
              </p>
              <Body className="mt-8 max-w-md">
                PentaVoice<sup>™</sup> brings together five complementary evidence streams to identify, validate, and translate patient voice into evidence that teams can act upon.
              </Body>
              <PentaVoiceDiagram />
            </div>
          }
          right={
            <div className="border border-[#E8E1D2] bg-[#FDFAF3]">
              {[
                "Digital & Social Listening",
                "PRO & Survey Evidence",
                "Patient Advisory Input",
                "Real-World Data & Evidence",
                "Regulatory & HTA Context",
              ].map((s, i, arr) => (
                <div
                  key={s}
                  className={`flex items-baseline gap-6 px-7 py-5 ${
                    i < arr.length - 1 ? "border-b border-[#E8E1D2]" : ""
                  }`}
                  data-testid={`pentavoice-stream-l${i + 1}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#C84C21] w-7 shrink-0">
                    L{i + 1}
                  </span>
                  <span className="font-serif text-[1.45rem] text-[#0F1825] leading-snug">
                    {s}
                  </span>
                </div>
              ))}
            </div>
          }
        />
      </Section>

      {/* 06 — How it works (truly merged: 4 verbs each with their layer owner) */}
      <Section id="how-it-works" eyebrow="06 · How it works" num="06">
        <Display>
          From signal to delivery.<span className="block italic">AI-enabled. Expert-validated.</span>
        </Display>
        <p className="mt-5 text-[14.5px] text-[#3F4856] max-w-4xl leading-relaxed">
          Patient voice moves through four stages — each owned by the layer best suited to do the work.
        </p>

        {/* Figure — process spine connecting the four stages */}
        <div className="mt-6 mb-1 hidden md:grid grid-cols-4 items-center relative" data-testid="deck-fig-process-spine">
          <div className="absolute left-[12.5%] right-[12.5%] top-[6px] h-px bg-[#D8CFB8]" />
          {["Detect", "Validate", "Translate", "Activate"].map((s, i) => (
            <div key={s} className="relative flex flex-col items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#C84C21] ring-4 ring-[#FAF7F1] relative z-10" />
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">
                {String(i + 1).padStart(2, "0")} · {s}
              </span>
            </div>
          ))}
        </div>

        {/* 4 verb steps, each carrying its layer attribution */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-px bg-[#E8E1D2] border border-[#E8E1D2]">
          {[
            {
              n: "01",
              v: "Detect",
              owner: "AI Signal Synthesis",
              d: "AI identifies and structures patient-relevant signals across digital, published, advisory, and real-world sources — tuned to asset, indication, and geography.",
            },
            {
              n: "02",
              v: "Validate",
              owner: "Expert Validation",
              d: "Senior HEOR and patient-evidence experts assess relevance, context, and consistency across evidence sources.",
            },
            {
              n: "03",
              v: "Translate",
              owner: "Expert Validation",
              d: "The same experts convert validated patient insights into evidence-ready themes, narratives, and strategic implications.",
            },
            {
              n: "04",
              v: "Activate",
              owner: "Strategic Delivery",
              d: "Curated briefings, reports, and workshops — not raw analytics — support decisions across the cross-functional teams that act.",
            },
          ].map((step) => (
            <div key={step.n} className="bg-white p-6 lg:p-7 flex flex-col">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">
                  {step.n}
                </span>
                <span className="font-mono text-[8.5px] uppercase tracking-[0.2em] text-[#A09680]">
                  {step.owner}
                </span>
              </div>
              <h3 className="font-serif text-[1.8rem] text-[#0F1825] mt-5 leading-tight">
                {step.v}
              </h3>
              <p className="mt-3 text-[12.5px] text-[#3F4856] leading-relaxed flex-1">{step.d}</p>
            </div>
          ))}
        </div>

        <p className="mt-7 text-[12.5px] text-[#5C6470] max-w-4xl leading-relaxed italic">
          AI-enabled detection · Expert-validated interpretation · Strategically delivered action.
        </p>
      </Section>

      {/* 07 — How the engagement is organised (closed-loop workflow) */}
      <Section id="engagement-flow" eyebrow="07 · How the engagement is organised" num="07" bg="white">
        <Display>
          A closed-loop engagement.
          <span className="block italic">From signal to action, every thread closed.</span>
        </Display>
        <p className="mt-5 text-[14px] text-[#3F4856] max-w-4xl leading-relaxed">
          Patient voice is captured, structured, interpreted, and translated into team-specific recommendations — every recommendation closes the loop inside this engagement. Delivered as a complete service, not a subscription to come back to.
        </p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { n: "01", t: "Patient Signals", cat: "Evidence", d: "Individual patient signals — raw voice, by stream." },
            { n: "02", t: "Concept Map", cat: "Evidence", d: "Signals become concepts and patient experience domains." },
            { n: "03", t: "Triangulation View", cat: "Synthesis", d: "One recommendation per theme, drawn across the streams." },
            { n: "04", t: "Team Recommendations", cat: "Translation", d: "Recommendations re-cast for each team that has to act." },
            { n: "05", t: "Engagement Deliverables", cat: "Delivery", d: "The reports your teams take away — and how to operationalise them." },
            { n: "06", t: "Evidence Traceability", cat: "Assurance", d: "Sources, expert review, methodology — on the record." },
          ].map((s) => (
            <div
              key={s.n}
              className="bg-[#FDFAF3] border border-[#E8E1D2] p-4 flex flex-col"
              data-testid={`engagement-step-${s.n}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">{s.n}</span>
                <span className="font-mono text-[8.5px] uppercase tracking-[0.22em] text-[#A09680]">{s.cat}</span>
              </div>
              <h3 className="font-serif text-[1.05rem] text-[#0F1825] leading-snug">{s.t}</h3>
              <p className="mt-2 text-[11px] text-[#3F4856] leading-relaxed flex-1">{s.d}</p>
            </div>
          ))}
        </div>

        <p className="mt-6 text-[12.5px] text-[#5C6470] max-w-4xl leading-relaxed italic">
          Triangulation View produces the recommendations. Team Recommendations re-casts them per owning team. Engagement Deliverables consolidates every take-away in one place.
        </p>
      </Section>

      {/* 08 — Pillars + Lifecycle (combined: how we interpret · when it matters) */}
      <Section id="pillars-lifecycle" eyebrow="08 · How we interpret · When it matters" num="08" compact>
        <Display>
          Two lenses on the same patient voice.
          <span className="block italic">Pillars structure interpretation. Lifecycle calibrates emphasis.</span>
        </Display>

        {/* Row A — Five Intelligence Pillars */}
        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-3">
            Intelligence pillars · How we interpret
          </div>
          <div className="border-t border-[#E8E1D2]">
            {[
              ["01", "Patient Voice Intelligence", "Narratives, burden signals, lived experience, QoL themes, unmet need."],
              ["02", "Evidence Intelligence", "Patient experience data, PRO / COA, HRQoL relevance, evidence planning."],
              ["03", "Medical Impact Intelligence", "MSL priorities, congress cues, scientific exchange themes."],
              ["04", "Access Intelligence", "HTA, payer, AMCP, ICER, value narrative, patient-relevant access evidence."],
              ["05", "Lifecycle Intelligence", "Pre-launch readiness, launch sentiment, adherence, switching, evolution."],
            ].map(([n, t, d]) => (
              <div
                key={n}
                className="grid grid-cols-12 gap-4 items-baseline py-2 border-b border-[#E8E1D2]"
              >
                <div className="col-span-12 md:col-span-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#C84C21]">
                  Pillar {n}
                </div>
                <div className="col-span-12 md:col-span-4 font-serif text-[1.05rem] text-[#0F1825] leading-tight">
                  {t}
                </div>
                <div className="col-span-12 md:col-span-6 text-[12px] text-[#3F4856] leading-relaxed">
                  {d}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Row B — Emphasis matrix · Pillar × Lifecycle stage */}
        <div className="mt-5" data-testid="deck-fig-emphasis-matrix">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">
              Emphasis matrix · Pillar × Lifecycle stage
            </div>
            <div className="flex items-center gap-1.5 font-mono text-[8.5px] uppercase tracking-[0.22em] text-[#A09680]">
              <span>Low</span>
              <span className="w-3.5 h-2 inline-block" style={{ backgroundColor: "rgba(200,76,33,0.10)" }} />
              <span className="w-3.5 h-2 inline-block" style={{ backgroundColor: "rgba(200,76,33,0.32)" }} />
              <span className="w-3.5 h-2 inline-block" style={{ backgroundColor: "rgba(200,76,33,0.58)" }} />
              <span className="w-3.5 h-2 inline-block" style={{ backgroundColor: "rgba(200,76,33,0.88)" }} />
              <span>High</span>
            </div>
          </div>
          <EmphasisMatrix />
        </div>
      </Section>

      {/* 09 — For Medical Affairs */}
      <FunctionSection
        id="for-ma"
        eyebrow="09 · For Medical Affairs"
        num="09"
        headline="Patient-grounded scientific exchange."
        intro="Patient voice translated into scientific narrative, congress strategy, and field-medical priorities."
        rows={[
          ["Scientific narratives", "grounded in patient experience and lived burden."],
          ["MSL engagement themes", "real-world frustrations, adherence drivers, and HCP-patient communication gaps."],
          ["Congress narrative cues", "patient priorities surfaced across digital, advisory, and PRO sources."],
          ["Medical communications", "patient-centered language that resonates with HCPs and patient communities."],
          ["Evidence planning", "patient-relevant gaps that inform RWE and PRO study design."],
        ]}
      />

      {/* 11 — For HEOR */}
      <FunctionSection
        id="for-heor"
        eyebrow="10 · For HEOR"
        num="10"
        bg="white"
        headline="Patient-relevant evidence for value & outcomes."
        intro="Patient voice integrated into value demonstration, evidence planning, and payer engagement."
        rows={[
          ["PRO / COA inputs", "qualitative signals informing instrument selection and concept elicitation."],
          ["HRQoL relevance", "patient-reported impact across symptom, function, and emotional domains."],
          ["Burden & unmet-need evidence", "structured signals for HTA, JCA, and payer narratives."],
          ["Value narrative inputs", "patient-relevant differentiators for value dossiers and AMCP submissions."],
          ["Real-world experience", "treatment journey, adherence, switching, and access barriers."],
        ]}
      />

      {/* 12 — Outputs */}
      <Section id="outputs" eyebrow="11 · What teams receive" num="11">
        <Display>
          Decision-ready outputs.<span className="block italic">One coherent engagement.</span>
        </Display>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E8E1D2] border border-[#E8E1D2]">
          {[
            ["01", "Patient Experience Insights", "A structured view of patient priorities, unmet needs, and treatment experience."],
            ["02", "Evidence Narratives", "Patient-centered evidence translated into decision-ready language."],
            ["03", "Evidence Gap Assessment", "Identification of areas where additional evidence may strengthen strategy."],
            ["04", "Strategic Recommendations", "Actionable guidance for Medical Affairs, HEOR, Market Access, Patient Engagement, and Brand teams."],
          ].map(([n, t, d]) => (
            <div key={n} className="bg-white p-8 lg:p-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-4">
                {n}
              </div>
              <h3 className="font-serif text-2xl text-[#0F1825] leading-snug">{t}</h3>
              <p className="mt-4 text-[14.5px] text-[#3F4856] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 13 — Methodologically anchored */}
      <Section id="methodology" eyebrow="12 · Methodologically anchored" num="12" bg="white">
        <TwoCol
          left={
            <>
              <Display>
                Built on <span className="italic">established evidence principles</span>.
              </Display>
              <Body className="mt-7">
                Voice2Evidence is informed by established approaches across patient-focused drug development, patient experience evidence, PRO methodology, real-world evidence, and health technology assessment.
              </Body>
              <Dim className="mt-5">
                Every engagement incorporates evidence triangulation, expert validation, and transparent interpretation of patient-relevant insights.
              </Dim>
            </>
          }
          right={
            <>
              <div className="bg-[#FDFAF3] border border-[#E8E1D2] p-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-5">
                  Informed by approaches across
                </div>
                <ul className="space-y-3">
                  {[
                    "Patient-focused drug development",
                    "Patient experience evidence",
                    "PRO / COA methodology",
                    "Real-world evidence",
                    "HTA and value assessment",
                    "Evidence synthesis and qualitative research",
                  ].map((d) => (
                    <li key={d} className="flex gap-3 items-baseline">
                      <span className="w-1 h-1 rounded-full bg-[#A09680] shrink-0 mt-2" />
                      <span className="font-serif text-[1.05rem] text-[#0F1825]">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-6 text-[11.5px] italic text-[#A09680] leading-relaxed">
                References to external frameworks are for methodological orientation only and do not imply endorsement, certification, approval, or formal affiliation.
              </p>
            </>
          }
        />
      </Section>

      {/* 14 — Published Work */}
      <Section id="published-work" eyebrow="13 · Selected published work" num="13" compact>
        <h2 className="font-serif font-normal text-xl md:text-2xl lg:text-[2rem] text-[#0F1825] leading-[1.1] tracking-tight max-w-5xl">
          Patient-voice evidence across{" "}
          <span className="italic">therapy areas, geographies, and decision forums.</span>
        </h2>
        {/* Figure — evidence footprint stats */}
        <div
          className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-b border-[#E8E1D2] py-2.5"
          data-testid="deck-fig-work-stats"
        >
          {[
            ["9", "Peer-reviewed & congress works"],
            ["4", "Decision forums"],
            ["7", "Therapy areas"],
            ["US · EU · Global", "Geographies"],
          ].map(([val, label]) => (
            <div key={label} className="flex flex-col">
              <span className="font-serif text-[1.35rem] text-[#0F1825] leading-none">{val}</span>
              <span className="mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.22em] text-[#A09680]">
                {label}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-0">
          {[
            {
              venue: "ISPOR 2026",
              date: "Philadelphia, USA · 17 – 20 May 2026",
              kind: "Accepted panel + posters",
              works: [
                { title: "Real-World Patient Voice at Scale: Can Social Media Listening Inform US Payer and Access Decisions?", focus: "Panel · US payer & access · credibility, integration, ethical guardrails" },
                { title: "Social Media Conversations on GLP-1 Therapies for Obesity", focus: "Obesity · GLP-1 · real-world experiences and treatment narratives" },
                { title: "Social Media Sentiment Toward Anti-Amyloid Therapies in Early Alzheimer's Disease", focus: "Alzheimer's · anti-amyloid · sentiment and decision-making" },
                { title: "Automated Social Media Listening on CGRP Preventive Therapies in Migraine", focus: "Migraine · CGRP preventive · automated listening" },
                { title: "Social Media Conversations on Immune Checkpoint Inhibitors", focus: "Immuno-oncology · safety, access, patient perspectives" },
                { title: "Real-World Online Conversations on MASH/NASH", focus: "MASH / NASH · 1-year longitudinal listening" },
              ],
            },
            {
              venue: "AMCP 2026",
              date: "Nashville, USA · 13 – 16 April 2026",
              kind: "Accepted poster",
              works: [
                { title: "Real-World Access and Affordability Barriers in Anal Cancer", focus: "Oncology · 5-year global access and affordability lens" },
              ],
            },
            {
              venue: "World EPA Congress 2026",
              date: "Amsterdam, Europe · 3 – 4 March 2026",
              kind: "Accepted poster",
              works: [
                { title: "Social Media Discussions of Probiotic Use in Irritable Bowel Syndrome", focus: "Gastroenterology · IBS · use patterns and patient-reported experiences" },
              ],
            },
            {
              venue: "Journal of Clinical Oncology",
              date: "Peer-reviewed · 2026",
              kind: "Publication · J Clin Oncol 44, 7 (2026)",
              works: [
                { title: "Sociocultural and educational determinants of delayed diagnosis in anal cancer", focus: "Oncology · 5-year global social media analysis · diagnostic delay" },
              ],
            },
          ].map((v) => (
            <div
              key={v.venue}
              className="grid grid-cols-12 gap-5 py-1.5 border-t border-[#E8E1D2]"
            >
              <div className="col-span-12 lg:col-span-3">
                <div className="font-serif text-[1.05rem] text-[#0F1825] leading-tight">{v.venue}</div>
                <div className="mt-1 font-mono text-[8px] uppercase tracking-[0.22em] text-[#A09680]">
                  {v.date}
                </div>
                <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.22em] text-[#C84C21]">
                  {v.kind}
                </div>
              </div>
              <ul className="col-span-12 lg:col-span-9 space-y-0.5">
                {v.works.map((w) => (
                  <li key={w.title}>
                    <div className="font-serif text-[0.85rem] text-[#0F1825] leading-snug">
                      {w.title}
                    </div>
                    <div className="font-mono text-[7pt] uppercase tracking-[0.18em] text-[#A09680]">
                      {w.focus}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* 15 — Begin a conversation */}
      <Section id="engage" eyebrow="14 · Begin a conversation" num="14" bg="white">
        <div className="grid grid-cols-12 gap-x-10 gap-y-10">
          <div className="col-span-12 lg:col-span-7">
            <Display size="xl">
              Confidential.<span className="block italic">Non-obligatory.</span>
            </Display>
            <Body className="mt-8 max-w-xl">
              Scoping conversations are sized to the asset, indication, lifecycle stage, geography, evidence need, and the cross-functional teams involved.
            </Body>
            <Body className="mt-4 max-w-xl">
              No platform license, no seat-based subscription — a scoped services engagement.
            </Body>
            <div className="mt-10 font-mono text-[12px] uppercase tracking-[0.26em] text-[#C84C21]">
              Voice → Evidence → Strategy
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:flex lg:justify-end">
            <div className="bg-[#FDFAF3] border border-[#E8E1D2] p-8 w-full lg:max-w-sm">
              <img
                src="/assets/logo/v2e-logo-light.png"
                alt="Voice2Evidence"
                className="h-7 w-auto"
              />
              <div className="w-10 h-[1.5px] bg-[#C84C21] my-5"></div>
              <div className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-[#A09680]">
                Web
              </div>
              <div className="font-serif text-[1.2rem] text-[#0F1825] mt-1">
                voice2evidence.com
              </div>
              <div className="mt-6 font-mono text-[9.5px] uppercase tracking-[0.24em] text-[#A09680]">
                An initiative by
              </div>
              <div className="font-serif text-[1.2rem] text-[#0F1825] mt-1">
                MarksMan Healthcare
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-7 bg-[#C84C21] hover:bg-[#A93D17] text-white font-mono text-[10px] uppercase tracking-[0.22em] px-5 py-3 transition-colors"
                data-testid="deck-cta-contact"
              >
                Request a briefing →
              </Link>
            </div>
          </div>
        </div>
        </Section>
      </div>
      {/* end horizontal track */}
    </div>
  );
}

// ----- Function-specific subsection (MA / HEOR) -------------------------------

function FunctionSection({ id, eyebrow, num, headline, intro, rows, bg = "cream" }) {
  return (
    <Section id={id} eyebrow={eyebrow} num={num} bg={bg}>
      <TwoCol
        left={
          <>
            <Display>{headline}</Display>
            <Body className="mt-7">{intro}</Body>
          </>
        }
        right={
          <ul className="border-t border-[#E8E1D2]">
            {rows.map(([label, desc]) => (
              <li
                key={label}
                className="grid grid-cols-12 gap-4 items-baseline py-4 border-b border-[#E8E1D2]"
              >
                <div className="col-span-12 md:col-span-5 font-serif text-[1.1rem] text-[#0F1825]">
                  {label}
                </div>
                <div className="col-span-12 md:col-span-7 text-[13.5px] text-[#3F4856] leading-relaxed">
                  {desc}
                </div>
              </li>
            ))}
          </ul>
        }
      />
    </Section>
  );
}

// ----- Figures ----------------------------------------------------------------

/**
 * PentaVoice hub-and-spoke diagram.
 * Five outer nodes (L1–L5) arranged around a central "Patient voice" hub.
 * Pure SVG — no external chart library — so the deck stays lightweight.
 */
function PentaVoiceDiagram() {
  const cx = 160;
  const cy = 110;
  const r = 68;
  const hubR = 28;
  const nodeR = 4;
  const labels = [
    "L1 · Digital",
    "L2 · PRO / Survey",
    "L3 · Advisory",
    "L4 · RWE / RWD",
    "L5 · Reg / HTA",
  ];
  // 5 nodes at 72° intervals, starting from the top
  const angles = [0, 72, 144, 216, 288].map((a) => ((a - 90) * Math.PI) / 180);
  const pts = angles.map((a) => ({
    x: cx + r * Math.cos(a),
    y: cy + r * Math.sin(a),
  }));
  return (
    <div className="mt-6" data-testid="deck-fig-pentavoice">
      <svg
        width="100%"
        height="200"
        viewBox="0 0 320 200"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="PentaVoice framework: five patient-voice evidence streams around a central hub"
      >
        {/* faint outer boundary ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E8E1D2" strokeDasharray="2 3" />
        {/* spokes */}
        {pts.map((p, i) => (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke="#D8CFB8"
            strokeWidth="1"
          />
        ))}
        {/* outer stream nodes */}
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={nodeR + 3} fill="#FAF7F1" />
            <circle cx={p.x} cy={p.y} r={nodeR} fill="#C84C21" />
          </g>
        ))}
        {/* hub */}
        <circle cx={cx} cy={cy} r={hubR} fill="#0F1825" />
        <text
          x={cx}
          y={cy - 3}
          textAnchor="middle"
          fontFamily="ui-serif, Georgia, 'Times New Roman', serif"
          fontSize="12"
          fill="#FAF7F1"
        >
          Patient
        </text>
        <text
          x={cx}
          y={cy + 12}
          textAnchor="middle"
          fontFamily="ui-serif, Georgia, 'Times New Roman', serif"
          fontSize="12"
          fill="#FAF7F1"
          fontStyle="italic"
        >
          voice
        </text>
        {/* node labels — positioned radially outside each node */}
        {pts.map((p, i) => {
          const a = angles[i];
          const lx = cx + (r + 20) * Math.cos(a);
          const ly = cy + (r + 20) * Math.sin(a);
          const cos = Math.cos(a);
          const anchor = Math.abs(cos) < 0.25 ? "middle" : cos > 0 ? "start" : "end";
          return (
            <text
              key={i}
              x={lx}
              y={ly}
              textAnchor={anchor}
              dominantBaseline="middle"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontSize="8.5"
              letterSpacing="1"
              fill="#5C6470"
            >
              {labels[i].toUpperCase()}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

/**
 * Emphasis matrix — pillar (row) × lifecycle stage (column).
 * Each cell is shaded by a 4-step intensity scale (Low / Med / High / Very high),
 * so the reader can see at a glance which pillar carries weight at which stage.
 */
function EmphasisMatrix() {
  const stages = [
    "Clinical Dev.",
    "Launch Readiness",
    "Regulatory & Evidence",
    "HTA & Access",
    "Post-Launch Growth",
  ];
  // Emphasis levels 1–4 (Low / Med / High / Very high) per pillar × stage
  const pillars = [
    ["Patient Voice Intelligence", [3, 2, 3, 4, 3]],
    ["Evidence Intelligence", [3, 2, 4, 4, 2]],
    ["Medical Impact Intelligence", [2, 4, 1, 2, 3]],
    ["Access Intelligence", [1, 2, 2, 4, 2]],
    ["Lifecycle Intelligence", [1, 3, 2, 2, 4]],
  ];
  const opacities = [0.10, 0.32, 0.58, 0.88];
  const labels = ["Low", "Med", "High", "V. high"];
  return (
    <div className="border border-[#E8E1D2] overflow-hidden">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr>
            <th className="w-[26%] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] bg-[#FDFAF3]">
              Pillar / Stage
            </th>
            {stages.map((s) => (
              <th
                key={s}
                className="px-2 py-2 font-mono text-[8.5px] uppercase tracking-[0.2em] text-[#5C6470] bg-[#FDFAF3] border-l border-[#E8E1D2] leading-tight"
              >
                {s}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pillars.map(([name, weights], rowI) => (
            <tr
              key={name}
              className={rowI < pillars.length - 1 ? "border-t border-[#E8E1D2]" : "border-t border-[#E8E1D2]"}
            >
              <td className="px-3 py-2 font-serif text-[0.85rem] text-[#0F1825] leading-tight bg-white">
                {name}
              </td>
              {weights.map((w, colI) => (
                <td
                  key={colI}
                  className="px-2 py-2 border-l border-[#E8E1D2] text-center"
                  style={{ backgroundColor: `rgba(200,76,33,${opacities[w - 1]})` }}
                >
                  <span
                    className="font-mono text-[8.5px] uppercase tracking-[0.14em]"
                    style={{ color: w >= 3 ? "#FAF7F1" : "#5C6470" }}
                  >
                    {labels[w - 1]}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
